import { Bus, constants, NotificationManager } from '@atlas/core';

import { openPaymentForm, openPaymentWindow } from 'src/common/redirect';

const BITCOIN_METHOD = 'coinspaid';
const GPAY_METHOD = 'gpay';
const PAYMAYA_METHOD = 'paymaya';
const SPEI = 'starspaySpeiDeposit';
const ATLAS_TRANSFER = 'atlasTransferDeposit';
const NO_IFRAME_DISPLAY_OPTION = 'NO_IFRAME';
const DEPOSIT_RESULT_FORM_TYPE = 'DEPOSIT_RESULT';

const getPopupNotificationData = (redirectUrl, paymentMethod) => {
  if (redirectUrl.startsWith(SPEI)) {
    return {
      data: {
        paymentMethod,
        redirectUrl,
      },
      message: 'speiDeposit',
      type: 'speiDeposit',
    };
  }

  return {
    data: {
      redirectUrl,
      text: 'pix_text',
      title: 'pix_title',
    },
    message: 'pix',
    type: 'pix',
  };
};

const getIsDepositResultForm = (processingType, paymentMethod, depositResultForm) =>
  processingType === constants.PAYMENT_PROCESSING.FORM &&
  depositResultForm &&
  paymentMethod?.transactionTtlInSeconds;

const getDepositResultNotification = ({
  additionalParams,
  amount,
  createTime,
  depositResultForm,
  paymentMethod,
  paymentOptions,
  publicPaymentId,
  redirectUrl,
}) => ({
  data: {
    amount,
    createTime,
    formId: depositResultForm.formId,
    interpolationValues: additionalParams,
    paymentMethod,
    ...(paymentOptions && { paymentOptions }),
    pixCode: redirectUrl,
    publicPaymentId,
  },
  type: 'pixDepositResult',
});

const parseSafetypaySteps = paymentInstructions =>
  paymentInstructions && JSON.parse(paymentInstructions);

const handleRedirectOrForm = ({
  data,
  displayOption,
  paymentMethod,
  processingType,
  redirectUrl,
  setRedirectUrl,
}) => {
  if (processingType === constants.PAYMENT_PROCESSING.REDIRECT && redirectUrl) {
    if (displayOption === NO_IFRAME_DISPLAY_OPTION) {
      setRedirectUrl?.(paymentMethod);
      window.location.href = redirectUrl;
      return false;
    }
    openPaymentWindow(redirectUrl);
  } else if (processingType === constants.PAYMENT_PROCESSING.FORM) {
    openPaymentForm(data);
  }

  return true;
};

export const depositSubmit = ({
  amount,
  data,
  paymentMethod = {},
  paymentOptions,
  setRedirectUrl,
}) => {
  const {
    createTime,
    displayOption = '',
    formData: { additionalParams = {} } = {},
    paymentFormName,
    paymentInstructions,
    paymentName,
    processingType,
    publicPaymentId,
    redirectUrl,
  } = data;

  const formId = paymentMethod.paymentForms?.[0]?.formId;

  const depositResultForm = paymentMethod?.paymentForms?.find(
    f => f.type === DEPOSIT_RESULT_FORM_TYPE,
  );

  if (getIsDepositResultForm(processingType, paymentMethod, depositResultForm)) {
    return NotificationManager.display(
      getDepositResultNotification({
        additionalParams,
        amount,
        createTime,
        depositResultForm,
        paymentMethod,
        paymentOptions,
        publicPaymentId,
        redirectUrl,
      }),
    );
  }

  if (processingType === constants.PAYMENT_PROCESSING.FORM && formId) {
    return NotificationManager.display({
      data: {
        amount,
        formId,
        interpolationValues: additionalParams,
        paymentMethod,
        publicPaymentId,
        type: constants.PAYMENT_OPERATION.DEPOSIT,
      },
      message: 'paymentsForm',
      type: 'paymentsForm',
    });
  }

  if (paymentFormName === 'safetypay') {
    return NotificationManager.display({
      data: {
        buttonText: 'deposit_safetypay_action_button',
        paymentName,
        redirectUrl,
        stepsPreText: 'deposit_safetypay_steps',
        stepsText: parseSafetypaySteps(paymentInstructions),
        text: 'deposit_safetypay_instruction',
        title: 'deposit_safetypay_header_title',
      },
      message: 'depositInstruction',
      type: 'depositInstruction',
    });
  }

  if (redirectUrl) {
    const redirectNotifications = [
      {
        name: BITCOIN_METHOD,
        notification: {
          data: {
            redirectUrl,
          },
          message: 'bitcoin',
          type: 'bitcoin',
        },
      },
      {
        name: GPAY_METHOD,
        notification: {
          data: { redirectUrl },
          message: 'gpay',
          type: 'gpay',
        },
      },
      {
        name: PAYMAYA_METHOD,
        notification: {
          data: { redirectUrl },
          message: 'paymaya',
          type: 'paymaya',
        },
      },
      {
        name: ATLAS_TRANSFER,
        notification: {
          data: { paymentName, publicPaymentId, redirectUrl },
          message: 'atlasTransfer',
          type: 'atlasTransfer',
        },
      },
    ];

    const redirectNotificationData = redirectNotifications.find(notification =>
      redirectUrl.startsWith(notification.name),
    );

    if (redirectNotificationData) {
      return NotificationManager.display(redirectNotificationData.notification);
    }
  }

  if (processingType === 'POPUP' && redirectUrl) {
    const popupNotificationData = getPopupNotificationData(redirectUrl, paymentMethod);

    return NotificationManager.display(popupNotificationData);
  }

  if (redirectUrl?.startsWith(ATLAS_TRANSFER)) {
    return NotificationManager.display({
      data: { paymentName, publicPaymentId, redirectUrl },
      message: 'atlasTransfer',
      type: 'atlasTransfer',
    });
  }

  const shouldBroadcast = handleRedirectOrForm({
    data,
    displayOption,
    paymentMethod,
    processingType,
    redirectUrl,
    setRedirectUrl,
  });

  if (shouldBroadcast) {
    // Broadcasting custom deposit submit event
    Bus.emit('profile:depositSubmit', data);
  }
};
